// utils/TourSyncUtils.js (renamed to EventSyncUtils.js)
import { doc, setDoc, serverTimestamp, getDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { getFirestoreInstance } from './firebaseHelper';

/**
 * Creates or updates a user_event_dates document
 * Works with both tours and shows
 * @param {string} userId - User ID
 * @param {string} eventId - Event ID (tour or show)
 * @param {string} DateDocId - Event date document ID
 * @param {Object} eventDate - Event date data
 * @param {Object} userData - User data
 * @param {Object} venueData - Venue data
 * @param {string} status - Status of the invitation (accepted, pending, etc.)
 * @param {string} eventType - Type of event ('tour' or 'show')
 * @returns {Promise<boolean>} Success status
 */
export async function updateUserEventDate(userId, eventId, dateDocId, eventDate, userData, venueData, status = 'accepted', eventType = 'tour') {
  // Only create entries for accepted invitations or for the creator
  if (status !== 'accepted' && userData?.userType !== 'Manager' && !userData?.isCreator) {
    console.log(`Skipping user_event_dates update for ${userId} - not accepted yet`);
    return false;
  }
  
  if (!userId || !eventId || !dateDocId || !eventDate) return false;
  
  try {
    const db = getFirestoreInstance();
    const docId = `${userId}_${eventId}_${dateDocId}`;
    const docRef = doc(db, 'user_event_dates', docId);
    
    // Get user's map privacy setting if not provided
    let mapPrivacy = "public"; // Default is now public
    if (userData?.mapPrivacy) {
      mapPrivacy = userData.mapPrivacy;
    } else {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          mapPrivacy = userDoc.data().mapPrivacy || "public";
        }
      } catch (err) {
        console.error('Error fetching user map privacy:', err);
      }
    }
    
    // Ensure we have the date string format (YYYY-MM-DD)
    let dateString = eventDate.date;
    // Handle different date formats
    if (typeof dateString === 'object' && dateString.seconds) {
      // Firestore timestamp
      dateString = new Date(dateString.seconds * 1000).toISOString().split('T')[0];
    } else if (typeof dateString === 'object' && typeof dateString.toDate === 'function') {
      // Firestore timestamp object
      dateString = dateString.toDate().toISOString().split('T')[0];
    } else if (typeof dateString === 'string' && dateString.includes('T')) {
      // ISO string with time
      dateString = dateString.split('T')[0];
    } else if (typeof dateString === 'string') {
      // Already in YYYY-MM-DD format
      dateString = dateString;
    } else if (dateString instanceof Date) {
      // JavaScript Date object
      dateString = dateString.toISOString().split('T')[0];
    }
    
    // Get event name fields based on event type
    let eventName = '';
    let actName = '';
    
    if (eventType === 'show') {
      eventName = eventDate.showName || eventDate.tourName || '';
    } else {
      eventName = eventDate.tourName || '';
    }
    
    actName = eventDate.actName || '';
    
    // Prepare document data
    const docData = {
      userId,
      eventId, // Used to be tourId
      eventType, // New field to differentiate between tours and shows
      dateDocId, // Used to be tourDateDocId
      date: dateString, // Store the date string (YYYY-MM-DD)
      mapPrivacy,
      venue: venueData || eventDate.event?.venueDetails?.venueData || {},
      isPublic: mapPrivacy === "public",
      fullName: userData?.fullName || '',
      profilePictureUrl: userData?.profilePictureUrl || '',
      userName: userData?.userName || '',
      userType: userData?.userType || '',
      userSubType: userData?.userSubType || '',
      eventName, // Used to be tourName, now handles both tour and show names
      actName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Set the document (creates or updates)
    await setDoc(docRef, docData);
    return true;
  } catch (error) {
    console.error('Error updating user_event_date:', error);
    return false;
  }
}

/**
 * Updates venue data for all users assigned to an event date
 * Works with both tours and shows
 * @param {string} eventId - Event ID (tour or show)
 * @param {string} dateDocId - Event date document ID
 * @param {Object} venueData - Updated venue data
 * @param {string} eventType - Type of event ('tour' or 'show')
 * @returns {Promise<boolean>} Success status
 */
export async function updateVenueForAllUsers(eventId, dateDocId, venueData, eventType = 'tour') {
  if (!eventId || !dateDocId || !venueData) return false;
  
  try {
    const db = getFirestoreInstance();
    
    // Query all documents for this event date
    const q = query(
      collection(db, 'user_event_dates'),
      where('eventId', '==', eventId),
      where('dateDocId', '==', dateDocId)
    );
    
    const snapshot = await getDocs(q);
    
    // Update each document with batch
    const batch = writeBatch(db);
    
    snapshot.forEach(docSnapshot => {
      batch.update(docSnapshot.ref, {
        venue: venueData,
        eventType: eventType, // Ensure eventType is set
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    return true;
  } catch (error) {
    console.error(`Error updating venue for all users (${eventType}):`, error);
    return false;
  }
}

